import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { CaretLeft, CaretRight, Tray } from "@phosphor-icons/react";
import {
  fetchInboxItems,
  selectInboxBucketCounts,
  selectInboxCategoryCounts,
  selectInboxFilters,
  selectInboxItems,
  selectInboxListError,
  selectInboxListStatus,
  selectInboxPagination,
} from "../../../slice/InboxSlice";
import useAuth from "../../../hooks/useAuth";
import InboxListItem from "./InboxListItem";
import InboxFolderFilter from "./InboxFolderFilter";
import InboxMatterSearch, { matterSearchLabel } from "./InboxMatterSearch";
import {
  INBOX_BUCKET_LABELS,
  INBOX_BUCKET_ORDER,
  INBOX_BUCKET_TITLES,
} from "../inboxTriage";
import {
  INBOX_SORT_PRIORITY,
  INBOX_SORT_RECEIVED,
  INBOX_TRIAGE_CAPABILITY,
  rememberInboxSort,
} from "../inboxSortPreference";
import {
  InboxFilterChip,
  InboxFiltersRow,
  InboxFolderCount,
  InboxListBody,
  InboxListCount,
  InboxListEmpty,
  InboxListEmptyDescription,
  InboxListEmptyTitle,
  InboxListErrorBanner,
  InboxListHeader,
  InboxListPane,
  InboxListTitle,
  InboxListTitleRow,
  InboxPaginationButton,
  InboxPaginationFooter,
  InboxPaginationPageForm,
  InboxPaginationPageInput,
  InboxPaginationPageLabel,
  InboxSelectionActions,
  InboxSelectionBar,
  InboxSelectionButton,
  InboxSkeletonBlock,
  InboxSkeletonRow,
  InboxSortGroup,
  InboxSortLabel,
} from "../InboxStyled";

/**
 * Left pane of the Inbox 3-pane layout.
 *
 * Owns:
 *   - Header (title, server-side total count, matter search, filter chips)
 *   - Body (one row per inbox item — see `InboxListItem`)
 *   - Pagination footer (page-number, prev/next)
 *
 * Reads list state from `InboxSlice`. Dispatches `fetchInboxItems` for
 * pagination + filter changes. The page mount itself owns the *initial*
 * fetch (so we can also seed the unread-count thunk on the same tick) —
 * this component is purely a renderer + paginator/filterer.
 *
 * Selected-row highlighting is purely URL-driven via `selectedDocumentId`
 * (mirrored from `useParams` in the page). We don't track "selected"
 * locally so cross-component reads (right rail header, viewer) all stay
 * in sync without a second source of truth.
 */
const SkeletonRows = () => (
  <>
    {Array.from({ length: 8 }).map((_, idx) => (
      <InboxSkeletonRow key={idx}>
        <InboxSkeletonBlock $w="80%" $h={14} />
        <InboxSkeletonBlock $w="100%" $h={11} />
        <InboxSkeletonBlock $w="50%" $h={10} />
      </InboxSkeletonRow>
    ))}
  </>
);

const InboxList = ({
  selectedDocumentId = null,
  onSelectDocument,
  checkedIds = [],
  onMarkSelectionReviewed,
  onClearChecked,
}) => {
  const dispatch = useDispatch();
  const items = useSelector(selectInboxItems);
  const status = useSelector(selectInboxListStatus);
  const error = useSelector(selectInboxListError);
  const pagination = useSelector(selectInboxPagination);
  const filters = useSelector(selectInboxFilters);
  const categoryCounts = useSelector(selectInboxCategoryCounts);
  const bucketCounts = useSelector(selectInboxBucketCounts);
  const [selectedMatter, setSelectedMatter] = useState(null);

  // Context+ (2026-10-21): the sort control and the three bucket chips
  // only mean something to a viewer the backend scores rows for — the
  // same capability that gates the Context panel. Everyone else keeps
  // the arrival-ordered list exactly as it was.
  const { hasCapability } = useAuth();
  const showTriageControls = hasCapability(INBOX_TRIAGE_CAPABILITY);

  const listArgs = useCallback(
    (overrides = {}) => ({
      matterId: filters.matterId,
      includeSnoozed: filters.includeSnoozed,
      recentlyReviewed: filters.recentlyReviewed,
      category: filters.category,
      sort: filters.sort,
      bucket: filters.bucket,
      page: pagination.page,
      pageSize: pagination.pageSize,
      ...overrides,
    }),
    [filters, pagination]
  );

  const handleSelectSort = useCallback(
    (nextSort) => {
      if (nextSort === filters.sort) return;
      rememberInboxSort(nextSort);
      dispatch(fetchInboxItems(listArgs({ sort: nextSort, page: 1 })));
    },
    [dispatch, filters.sort, listArgs]
  );

  const handleToggleBucket = useCallback(
    (bucket) => {
      const nextBucket = filters.bucket === bucket ? null : bucket;
      dispatch(fetchInboxItems(listArgs({ bucket: nextBucket, page: 1 })));
    },
    [dispatch, filters.bucket, listArgs]
  );

  const handleSelectMatter = useCallback(
    (matter) => {
      setSelectedMatter({
        id: matter.id,
        label: matterSearchLabel(matter),
      });
      dispatch(fetchInboxItems(listArgs({ matterId: matter.id, page: 1 })));
    },
    [dispatch, listArgs]
  );

  const handleClearMatter = useCallback(() => {
    setSelectedMatter(null);
    dispatch(fetchInboxItems(listArgs({ matterId: null, page: 1 })));
  }, [dispatch, listArgs]);

  const handleToggleSnoozed = useCallback(() => {
    dispatch(
      fetchInboxItems(
        listArgs({
          includeSnoozed: !filters.includeSnoozed,
          page: 1,
        })
      )
    );
  }, [dispatch, filters.includeSnoozed, listArgs]);

  const handleToggleReviewed = useCallback(() => {
    dispatch(
      fetchInboxItems(
        listArgs({
          recentlyReviewed: !filters.recentlyReviewed,
          page: 1,
        })
      )
    );
  }, [dispatch, filters.recentlyReviewed, listArgs]);

  const handleSelectFolder = useCallback(
    (nextCategory) => {
      dispatch(fetchInboxItems(listArgs({ category: nextCategory, page: 1 })));
    },
    [dispatch, listArgs]
  );

  const handlePrev = useCallback(() => {
    if (!pagination.hasPrevious || pagination.page <= 1) return;
    dispatch(fetchInboxItems(listArgs({ page: pagination.page - 1 })));
  }, [dispatch, listArgs, pagination]);

  const handleNext = useCallback(() => {
    if (!pagination.hasNext) return;
    dispatch(fetchInboxItems(listArgs({ page: pagination.page + 1 })));
  }, [dispatch, listArgs, pagination]);

  const handleRetry = useCallback(() => {
    dispatch(fetchInboxItems(listArgs()));
  }, [dispatch, listArgs]);

  const handleGoToPage = useCallback(
    (targetPage) => {
      const clamped = Math.min(
        Math.max(targetPage, 1),
        pagination.totalPages
      );
      if (Number.isNaN(clamped) || clamped === pagination.page) return;
      dispatch(fetchInboxItems(listArgs({ page: clamped })));
    },
    [dispatch, listArgs, pagination]
  );

  // "Page X of Y" doubles as a jump-to-page control: clicking it swaps the
  // label for a small number input (Enter/blur commits, Escape cancels).
  const [isEditingPage, setIsEditingPage] = useState(false);
  const [pageInputValue, setPageInputValue] = useState("");
  const pageInputRef = useRef(null);

  useEffect(() => {
    if (isEditingPage && pageInputRef.current) {
      pageInputRef.current.focus();
      pageInputRef.current.select();
    }
  }, [isEditingPage]);

  const handlePageLabelClick = useCallback(() => {
    setPageInputValue(String(pagination.page));
    setIsEditingPage(true);
  }, [pagination.page]);

  const commitPageInput = useCallback(() => {
    const parsed = parseInt(pageInputValue, 10);
    if (!Number.isNaN(parsed)) {
      handleGoToPage(parsed);
    }
    setIsEditingPage(false);
  }, [pageInputValue, handleGoToPage]);

  const handlePageInputKeyDown = useCallback((event) => {
    if (event.key === "Escape") {
      setIsEditingPage(false);
    }
  }, []);

  // First-mount skeleton — keep showing real rows on subsequent re-fetches
  // (page change, filter toggle) so the page doesn't flash a skeleton on
  // every navigation. No rows and either a fetch in flight or none asked
  // for yet (the page waits for the signed-in user before its first
  // fetch) means we genuinely have nothing to show yet — never "Your
  // inbox is clear" while the answer is still on its way.
  const isInitialLoading =
    (status === "loading" || status === "idle") && (!items || items.length === 0);

  return (
    <InboxListPane aria-label="Attorney inbox list">
      <InboxListHeader>
        <InboxListTitleRow>
          <InboxListTitle>Inbox</InboxListTitle>
          {pagination.count > 0 && (
            <InboxListCount aria-live="polite">
              {pagination.count.toLocaleString()} item
              {pagination.count === 1 ? "" : "s"}
            </InboxListCount>
          )}
        </InboxListTitleRow>
        <InboxMatterSearch
          selectedMatterId={selectedMatter?.id ?? filters.matterId}
          onSelect={handleSelectMatter}
          onClear={handleClearMatter}
        />
        <InboxFolderFilter
          category={filters.category}
          counts={categoryCounts}
          onSelect={handleSelectFolder}
        />
        <InboxFiltersRow>
          <InboxFilterChip
            type="button"
            $active={filters.includeSnoozed}
            onClick={handleToggleSnoozed}
            aria-pressed={filters.includeSnoozed}
          >
            {filters.includeSnoozed ? "Showing snoozed" : "Show snoozed"}
          </InboxFilterChip>
          <InboxFilterChip
            type="button"
            $active={filters.recentlyReviewed}
            onClick={handleToggleReviewed}
            aria-pressed={filters.recentlyReviewed}
            title="Mail you marked reviewed in the past two weeks, most recent first"
          >
            Recently reviewed
          </InboxFilterChip>
        </InboxFiltersRow>
        {showTriageControls && (
          <InboxFiltersRow aria-label="Priority sort and buckets">
            <InboxSortGroup role="radiogroup" aria-label="Sort inbox by">
              <InboxSortLabel aria-hidden="true">Sort</InboxSortLabel>
              <InboxFilterChip
                type="button"
                role="radio"
                aria-checked={filters.sort === INBOX_SORT_PRIORITY}
                $active={filters.sort === INBOX_SORT_PRIORITY}
                disabled={filters.recentlyReviewed}
                onClick={() => handleSelectSort(INBOX_SORT_PRIORITY)}
                title={
                  filters.recentlyReviewed
                    ? "The reviewed pile is ordered by when you reviewed it"
                    : "What matters first: closing windows, proposed reductions, decisions with issues denied"
                }
              >
                Priority
              </InboxFilterChip>
              <InboxFilterChip
                type="button"
                role="radio"
                aria-checked={filters.sort !== INBOX_SORT_PRIORITY}
                $active={filters.sort !== INBOX_SORT_PRIORITY}
                disabled={filters.recentlyReviewed}
                onClick={() => handleSelectSort(INBOX_SORT_RECEIVED)}
                title="Most recently arrived first"
              >
                Newest
              </InboxFilterChip>
            </InboxSortGroup>
            {INBOX_BUCKET_ORDER.map((bucket) => {
              const count = bucketCounts?.[bucket];
              return (
                <InboxFilterChip
                  key={bucket}
                  type="button"
                  $active={filters.bucket === bucket}
                  aria-pressed={filters.bucket === bucket}
                  onClick={() => handleToggleBucket(bucket)}
                  title={INBOX_BUCKET_TITLES[bucket]}
                >
                  {INBOX_BUCKET_LABELS[bucket]}
                  {typeof count === "number" && (
                    <InboxFolderCount>{count.toLocaleString()}</InboxFolderCount>
                  )}
                </InboxFilterChip>
              );
            })}
          </InboxFiltersRow>
        )}
      </InboxListHeader>

      {error && (
        <InboxListErrorBanner role="alert">
          {error}{" "}
          <button
            type="button"
            onClick={handleRetry}
            style={{
              background: "none",
              border: "none",
              color: "#A61412",
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "underline",
              padding: 0,
            }}
          >
            Retry
          </button>
        </InboxListErrorBanner>
      )}

      {checkedIds.length > 1 && (
        <InboxSelectionBar>
          <span>
            {checkedIds.length} selected
          </span>
          <InboxSelectionActions>
            <InboxSelectionButton
              type="button"
              onClick={onMarkSelectionReviewed}
            >
              Mark reviewed
            </InboxSelectionButton>
            <InboxSelectionButton type="button" onClick={onClearChecked}>
              Clear
            </InboxSelectionButton>
          </InboxSelectionActions>
        </InboxSelectionBar>
      )}

      <InboxListBody>
        {isInitialLoading ? (
          <SkeletonRows />
        ) : items.length === 0 ? (
          <InboxListEmpty role="status">
            <Tray size={32} weight="duotone" color="#7688A3" />
            <InboxListEmptyTitle>
              {filters.bucket
                ? `Nothing in ${INBOX_BUCKET_LABELS[filters.bucket] || "this pile"}`
                : filters.recentlyReviewed
                ? "Nothing reviewed lately"
                : "Your inbox is clear"}
            </InboxListEmptyTitle>
            <InboxListEmptyDescription>
              {filters.bucket
                ? "No scored mail in this pile. Mail the AI has not read yet shows only when no pile is picked."
                : filters.recentlyReviewed
                ? "Nothing marked reviewed in the past two weeks."
                : selectedMatter
                ? `Nothing in the inbox for ${selectedMatter.label}.`
                : filters.includeSnoozed
                ? "Nothing to review — including snoozed items."
                : "Nothing to review. Snoozed items aren't shown by default."}
            </InboxListEmptyDescription>
          </InboxListEmpty>
        ) : (
          items.map((item) => (
            <InboxListItem
              key={item.id}
              item={item}
              selected={
                selectedDocumentId != null &&
                // String comparison: document ids are UUIDs post
                // split-schema; Number() made this NaN === NaN.
                String(selectedDocumentId) === String(item.id)
              }
              checked={checkedIds.some(
                (id) => String(id) === String(item.id)
              )}
              onSelect={onSelectDocument}
            />
          ))
        )}
      </InboxListBody>

      {pagination.totalPages > 1 && (
        <InboxPaginationFooter>
          {isEditingPage ? (
            <InboxPaginationPageForm
              onSubmit={(event) => {
                event.preventDefault();
                commitPageInput();
              }}
            >
              Page{" "}
              <InboxPaginationPageInput
                ref={pageInputRef}
                type="number"
                inputMode="numeric"
                min={1}
                max={pagination.totalPages}
                value={pageInputValue}
                onChange={(event) => setPageInputValue(event.target.value)}
                onKeyDown={handlePageInputKeyDown}
                onBlur={commitPageInput}
                aria-label="Go to page"
              />{" "}
              of <strong>{pagination.totalPages}</strong>
            </InboxPaginationPageForm>
          ) : (
            <InboxPaginationPageLabel
              type="button"
              onClick={handlePageLabelClick}
              aria-label="Jump to a specific page"
            >
              Page <strong>{pagination.page}</strong> of{" "}
              <strong>{pagination.totalPages}</strong>
            </InboxPaginationPageLabel>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <InboxPaginationButton
              type="button"
              onClick={handlePrev}
              disabled={!pagination.hasPrevious}
              aria-label="Previous page"
            >
              <CaretLeft size={12} weight="bold" />
              Prev
            </InboxPaginationButton>
            <InboxPaginationButton
              type="button"
              onClick={handleNext}
              disabled={!pagination.hasNext}
              aria-label="Next page"
            >
              Next
              <CaretRight size={12} weight="bold" />
            </InboxPaginationButton>
          </div>
        </InboxPaginationFooter>
      )}
    </InboxListPane>
  );
};

InboxList.propTypes = {
  selectedDocumentId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onSelectDocument: PropTypes.func.isRequired,
  checkedIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onMarkSelectionReviewed: PropTypes.func,
  onClearChecked: PropTypes.func,
};

export default InboxList;
